import { apiClient } from "@/api/apiClient";
import type { Account, AccountCreate, AccountUpdate, ConnectionTestResult } from "@/types/account";

class AccountService {
  async getAccounts(): Promise<Account[]> {
    try {
      const response = await apiClient.get("/v1/accounts");
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response.data?.accounts)) return response.data.accounts;
      throw new Error("Некорректный ответ сервиса аккаунтов");
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAccount(accountUuid: string): Promise<Account> {
    try {
      const response = await apiClient.get<Account>(`/v1/accounts/${accountUuid}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createAccount(data: AccountCreate): Promise<Account> {
    try {
      const response = await apiClient.post<Account>("/v1/accounts", data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateAccount(accountUuid: string, data: AccountUpdate): Promise<Account> {
    try {
      const response = await apiClient.put<Account>(`/v1/accounts/${accountUuid}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteAccount(accountUuid: string): Promise<void> {
    try {
      await apiClient.delete(`/v1/accounts/${accountUuid}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testConnection(accountUuid: string): Promise<ConnectionTestResult> {
    try {
      const response = await apiClient.post<ConnectionTestResult>(
        `/v1/accounts/${accountUuid}/test-connection`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async testConnectionWithCredentials(credentials: AccountCreate): Promise<ConnectionTestResult> {
    try {
      const response = await apiClient.post<ConnectionTestResult>(
        `/v1/accounts/test-connection`,
        credentials
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async syncAccount(accountUuid: string): Promise<void> {
    try {
      await apiClient.post(`/v1/accounts/${accountUuid}/sync`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateAccountInfo(accountUuid: string): Promise<void> {
    try {
      await apiClient.post(`/v1/accounts/${accountUuid}/update-info`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    const axiosError = error as {
      response?: { data?: { detail?: string } };
      request?: unknown;
    };

    if (axiosError.response) {
      const message = axiosError.response.data?.detail || "Ошибка при выполнении запроса";
      return new Error(message);
    } else if (axiosError.request) {
      return new Error("Сервер не отвечает. Проверьте интернет-соединение.");
    }
    return new Error("Произошла ошибка. Попробуйте снова позже.");
  }
}

export const accountService = new AccountService();
